import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { 
  ArrowLeft, 
  Gift, 
  Users, 
  Copy, 
  Mail, 
  MessageCircle, 
  Share2,
  CheckCircle2,
  TrendingUp,
  Star
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';

const Referral = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    successfulReferrals: 0,
    creditsEarned: 0
  });

  const referralLink = referralCode ? `${window.location.origin}/auth?ref=${referralCode}` : '';

  // Fetch or create referral code from database
  useEffect(() => {
    const fetchReferralData = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }


      try {
        setIsLoading(true);
        
        // Fetch referral code
        const { data: referralData, error: referralError } = await supabase
          .from('referrals')
          .select('*')
          .eq('user_id', user.id)
          .single();

        if (referralError && referralError.code !== 'PGRST116') {
          console.error('Error fetching referral:', referralError);
          toast({
            title: "Error",
            description: "Failed to load referral data",
            variant: "destructive"
          });
          return;
        }

        if (referralData) {
          setReferralCode(referralData.referral_code);
          setReferralStats({
            totalReferrals: referralData.total_referrals || 0,
            successfulReferrals: referralData.successful_referrals || 0,
            creditsEarned: referralData.credits_earned || 0
          });
        } else {
          // No referral code exists, create one
          const newReferralCode = `ODYVOY${Math.random().toString(36).substring(2, 10).toUpperCase()}`;
          
          const { data: newReferral, error: createError } = await supabase
            .from('referrals')
            .insert({
              user_id: user.id,
              referral_code: newReferralCode,
              total_referrals: 0,
              successful_referrals: 0,
              credits_earned: 0
            })
            .select()
            .single();

          if (createError) {
            console.error('Error creating referral:', createError);
            toast({
              title: "Error",
              description: "Failed to create referral code",
              variant: "destructive"
            });
            return;
          }

          if (newReferral) {
            setReferralCode(newReferral.referral_code);
            setReferralStats({
              totalReferrals: 0,
              successfulReferrals: 0,
              creditsEarned: 0
            });
          }
        }
      } catch (error) {
        console.error('Error in fetchReferralData:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralData();
  }, [user, toast]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({
      title: "Copied!",
      description: "Referral link copied to clipboard",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent("Get 10% OFF Your First OdyVoy Subscription!");
    const body = encodeURIComponent(
      `Hey!\n\nI've been using OdyVoy to plan my trips, and it's honestly a game changer! I wanted to share this exclusive offer with you.\n\nUse my referral link to get 10% OFF your first Premium Economy or First Class subscription:\n👉 ${referralLink}\n\nOdyVoy helps you pack smarter, manage budgets, stay wellness-ready, and explore local tips—all in one app. Plus, when you upgrade using my link, I get a free month too. Win-win!\n\nDon't miss out on this exclusive discount.\nHappy travels! 🌍✈️`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(
      `Hey!\n\nI've been using OdyVoy to plan my trips, and it's honestly a game changer! I wanted to share this exclusive offer with you.\n\nUse my referral link to get 10% OFF your first Premium Economy or First Class subscription:\n👉 ${referralLink}\n\nOdyVoy helps you pack smarter, manage budgets, stay wellness-ready, and explore local tips—all in one app. Plus, when you upgrade using my link, I get a free month too. Win-win!\n\nDon't miss out on this exclusive discount.\nHappy travels! 🌍✈️`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const benefits = [
    {
      icon: Gift,
      title: '1 Month Free for You',
      description: 'Get 1 free month for each friend who subscribes to Premium Economy or First Class'
    },
    {
      icon: Users,
      title: 'Unlimited Referrals',
      description: 'No limit! Refer as many friends as you want and stack your rewards'
    },
    {
      icon: Star,
      title: '10% Discount for Friends',
      description: 'Your friends get 10% OFF their first subscription when they use your referral link'
    }
  ];

  const steps = [
    'Share your unique referral link with friends and family',
    'They sign up using your link and get 10% OFF when they upgrade to Premium or First Class',
    'You get 1 month free added to your account automatically once they subscribe',
    'Keep referring to earn more free months!'
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading referral data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Refer Friends & Get 1 Month Free | OdyVoy Referral Program</title>
        <meta name="description" content="Share OdyVoy with friends and earn free months! Get 1 month free for every friend who subscribes. Your friends get 10% off their first subscription." />
        <link rel="canonical" href="https://odyvoy.com/referral" />
        <meta property="og:title" content="OdyVoy Referral Program - Get 1 Month Free" />
        <meta property="og:description" content="Share OdyVoy and earn free months. Your friends get 10% off their first subscription!" />
        <meta property="og:url" content="https://odyvoy.com/referral" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="OdyVoy Referral Program - Get 1 Month Free" />
        <meta name="twitter:description" content="Share OdyVoy and earn free months. Your friends get 10% off!" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "OdyVoy Referral Program",
            "description": "Refer friends to OdyVoy and earn free subscription months",
            "url": "https://odyvoy.com/referral",
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy"
            }
          })}
        </script>
      </Helmet>

      {/* Header */}
      <header className="border-b border-border sticky top-0 bg-background/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 min-h-[44px] h-8 w-auto md:h-10 md:w-auto px-3 md:px-4 touch-manipulation active:scale-95"
              aria-label="Back to Home"
            >
              <ArrowLeft className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              <span className="text-xs md:text-sm font-semibold">Back to Home</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-accent/5 py-8 sm:py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-3 sm:mb-4 bg-gradient-to-r from-primary to-accent text-white border-0 text-xs sm:text-sm">
            <Gift className="h-3 w-3 mr-1 flex-shrink-0" />
            Referral Rewards
          </Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 px-2">
            Share OdyVoy,
            <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Get 1 Month Free!
            </span>
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-muted-foreground max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
            For every friend you invite who upgrades to Premium Economy or First Class, you'll receive a free month — our treat!
          </p>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl mx-auto">
            <Card className="text-center">
              <CardContent className="pt-3 sm:pt-4 md:pt-6 pb-3 sm:pb-4 md:pb-6 px-2 sm:px-4">
                <Users className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mx-auto mb-1 sm:mb-2 text-primary flex-shrink-0" />
                <p className="text-xl sm:text-2xl font-bold">{referralStats.totalReferrals}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Total Referrals</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-3 sm:pt-4 md:pt-6 pb-3 sm:pb-4 md:pb-6 px-2 sm:px-4">
                <CheckCircle2 className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mx-auto mb-1 sm:mb-2 text-green-500 flex-shrink-0" />
                <p className="text-xl sm:text-2xl font-bold">{referralStats.successfulReferrals}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Successful</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-3 sm:pt-4 md:pt-6 pb-3 sm:pb-4 md:pb-6 px-2 sm:px-4">
                <Gift className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 mx-auto mb-1 sm:mb-2 text-yellow-500 flex-shrink-0" />
                <p className="text-xl sm:text-2xl font-bold">{referralStats.creditsEarned}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground">Months Earned</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12 space-y-6 sm:space-y-8">
        {/* Share Your Link */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Share2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
              Your Unique Referral Link
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Share this link with friends to start earning rewards
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={referralLink}
                readOnly
                className="font-mono text-xs sm:text-sm min-h-[44px]"
              />
              <Button 
                onClick={handleCopyLink} 
                className="flex-shrink-0 min-h-[44px] touch-manipulation active:scale-95 w-full sm:w-auto"
              >
                {copied ? <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> : <Copy className="h-4 w-4 flex-shrink-0" />}
                <span className="ml-2">{copied ? 'Copied!' : 'Copy'}</span>
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button 
                variant="outline" 
                onClick={handleShareEmail} 
                className="flex-1 min-h-[44px] touch-manipulation active:scale-95"
              >
                <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-xs sm:text-sm">Share via Email</span>
              </Button>
              <Button 
                variant="outline" 
                onClick={handleShareWhatsApp} 
                className="flex-1 min-h-[44px] touch-manipulation active:scale-95"
              >
                <MessageCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="text-xs sm:text-sm">Share via WhatsApp</span>
              </Button>
            </div>

            <div className="bg-secondary/50 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-muted-foreground mb-2">Your Referral Code:</p>
              <p className="text-xl sm:text-2xl font-bold font-mono text-primary break-all">{referralCode}</p>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card>
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
              How It Works
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Start earning free months in 4 simple steps
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 pt-0">
            <ol className="space-y-3 sm:space-y-4">
              {steps.map((step, index) => (
                <li key={index} className="flex gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <p className="text-xs sm:text-sm pt-1.5 sm:pt-1">{step}</p>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* Benefits */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index}>
                <CardHeader className="p-4 sm:p-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-primary flex-shrink-0" />
                  </div>
                  <CardTitle className="text-base sm:text-lg">{benefit.title}</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">{benefit.description}</CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* Terms */}
        <Card className="bg-secondary/20">
          <CardHeader className="p-4 sm:p-6">
            <CardTitle className="text-base sm:text-lg">Program Terms</CardTitle>
          </CardHeader>
          <CardContent className="text-xs sm:text-sm text-muted-foreground space-y-2 p-4 sm:p-6 pt-0">
            <p>• Your friend gets 10% OFF their first Premium Economy or First Class subscription when using your link</p>
            <p>• You receive 1 month free once your friend successfully subscribes</p>
            <p>• The 1 month free credit will be added to your account within 24 hours of your friend's successful subscription</p>
            <p>• Free months are applied as subscription credits and extend your current subscription</p>
            <p>• The 10% discount is automatically applied at checkout for new subscribers using the referral link</p>
            <p>• There is no limit to the number of referrals you can make</p>
            <p>• Referral credits are non-transferable and have no cash value</p>
            <p>• OdyVoy reserves the right to modify or terminate the referral program at any time</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Referral;